// What the Portal shows for a workshop depollution checklist (Stage 3a plan §D): the state chip,
// the deadline chip, the counters, the blocking text, the vehicle list cell and the task screen
// (selected and neighbour tasks, task status, hint, groups, approval panel). Plain module with
// no Vue, so it runs under `node --test`. Labels are i18n keys with their params.
import { formatCivilDate } from "../Acceptance/deadlines.mjs";

// State -> chip colour: Non iniziata grey, In corso blue, Da approvare amber, Approvata green.
export const WORKSHOP_STATE_COLORS = {
  not_started: "grey",
  in_progress: "blue",
  to_approve: "amber",
  approved: "green",
};

const DEADLINE_COLORS = {
  due: "grey",
  near: "amber",
  overdue: "red",
  approved_on_time: "green",
  approved_late: "orange",
};

// The Bonifica card on the acceptance page: only with the workshop on and a confirmed dossier.
export function usesDepollutionCard({ workshopEnabled, dossierStatus }) {
  return workshopEnabled === true && dossierStatus === "confirmed";
}

// The label key of a template task.
export function taskKey(code) {
  return `workshop.task.${code}`;
}

// { key, color } of the state chip, or null for an unknown state.
export function stateChip(state) {
  if (!WORKSHOP_STATE_COLORS[state]) return null;
  return {
    key: `acceptance.depollution.states.${state}`,
    color: WORKSHOP_STATE_COLORS[state],
  };
}

// { key, params: { date, days }, color } of the deadline chip from the view's deadline, or null
// without a due date: "Entro DD/MM/YYYY · n gg lav." (grey, amber when near), "Scade oggi" on the
// due date, red "Scaduta dal" when overdue, and on time / late once approved.
export function deadlineChip(deadline) {
  const status = deadline && deadline.status;
  const date = formatCivilDate(deadline && deadline.dueDate);
  if (!date || !DEADLINE_COLORS[status]) return null;
  let key = `acceptance.depollution.deadline.${status}`;
  if (status === "due" || status === "near") {
    key =
      deadline.workingDaysLeft === 0
        ? "acceptance.depollution.deadline.today"
        : "acceptance.depollution.deadline.due";
  }
  return {
    key,
    params: { date, days: deadline.workingDaysLeft },
    color: DEADLINE_COLORS[status],
  };
}

// { done, total, remaining, percent, currentTaskCode } from the view's progress (mandatory tasks).
export function counters(view) {
  const progress = (view && view.progress) || {};
  const done = progress.done || 0;
  const total = progress.total || 0;
  return {
    done,
    total,
    remaining: Math.max(total - done, 0),
    percent: total ? Math.round((done * 100) / total) : 0,
    currentTaskCode: progress.currentTaskCode || null,
  };
}

// Whether the view carries a warning (deadline_near, overdue, estimated_quantity, quarantine).
export function hasWarning(view, code) {
  return Boolean(
    view &&
      (view.warnings || []).some((warning) => warning && warning.code === code),
  );
}

// What still blocks the approval, as one line { key, params }, shown only when it adds something
// to the "mandatory tasks to do" line: exceptions to accept, with the missing tasks only when
// there are some; "ready" when nothing blocks. Null when only mandatory tasks are missing (the
// card already says how many) and once approved.
export function blockingText(view) {
  if (!view || view.state === "approved") return null;
  const blocking = (view.approvalCheck && view.approvalCheck.blocking) || [];
  const count = (code) => {
    const item = blocking.find((entry) => entry && entry.code === code);
    return item ? (item.taskCodes || []).length : 0;
  };
  const missing = count("mandatory_missing");
  const exceptions = count("exception_pending");
  if (!missing && !exceptions) {
    return { key: "acceptance.depollution.ready", params: {} };
  }
  if (!exceptions) return null;
  if (!missing) {
    return {
      key: "acceptance.depollution.blocking_exceptions",
      params: { exceptions },
    };
  }
  return {
    key: "acceptance.depollution.blocking",
    params: { missing, exceptions },
  };
}

// [{ resultId, taskCode, reason }] of the exceptions waiting for acceptance, in template order.
export function pendingExceptionRows(view) {
  const tasks = (view && view.tasks) || [];
  return ((view && view.pendingExceptions) || []).map(
    ({ resultId, taskCode }) => {
      const task = tasks.find((item) => item.code === taskCode);
      const current = task && task.current;
      return {
        resultId,
        taskCode,
        reason: (current && current.id === resultId && current.reason) || "",
      };
    },
  );
}

// The label key of a template group.
export function groupKey(code) {
  return `workshop.group.${code}`;
}

// The task the screen opens on (D2): the first task in template order without a current result
// (the view's progress.currentTaskCode), or the first task when all have one. Null without tasks.
export function initialTaskCode(view) {
  const tasks = (view && view.tasks) || [];
  const current = view && view.progress && view.progress.currentTaskCode;
  if (current && tasks.some((task) => task.code === current)) return current;
  return tasks.length ? tasks[0].code : null;
}

// After a reload the selected task stays when it still exists.
export function keepSelection(view, code) {
  const tasks = (view && view.tasks) || [];
  return code && tasks.some((task) => task.code === code)
    ? code
    : initialTaskCode(view);
}

// { previous, next } task codes around the selected one, null at either end.
export function neighbourTasks(view, code) {
  const tasks = (view && view.tasks) || [];
  const index = tasks.findIndex((task) => task.code === code);
  if (index < 0) return { previous: null, next: null };
  return {
    previous: index > 0 ? tasks[index - 1].code : null,
    next: index < tasks.length - 1 ? tasks[index + 1].code : null,
  };
}

const RESULT_STATUS = {
  completed: { key: "workshop.status.completed", color: "green" },
  not_present: { key: "workshop.status.not_present", color: "grey" },
  not_applicable: { key: "workshop.status.not_applicable", color: "grey" },
};

// { key, color } of a task's status chip: its current result ("Eseguita", "Non presente",
// "N.A.", "Eccezione" amber, "Eccezione accettata" green), "Attuale" for the progress' current
// task, "—" otherwise.
export function taskStatus(task, currentTaskCode) {
  const current = task && task.current;
  if (current) {
    if (current.result === "exception") {
      return current.acceptance
        ? { key: "workshop.status.exception_accepted", color: "green" }
        : { key: "workshop.status.exception", color: "amber" };
    }
    if (RESULT_STATUS[current.result]) return RESULT_STATUS[current.result];
  }
  if (task && task.code === currentTaskCode) {
    return { key: "workshop.status.current", color: "blue" };
  }
  return { key: "workshop.status.none", color: "grey" };
}

// The order hint of a task (M1): { key, target } while a suggested task has no current result;
// "Vai" selects target. Advisory only: nothing is disabled. Null without a hint.
export function taskHint(task) {
  const hint = task && task.hint;
  const codes = (hint && hint.taskCodes) || [];
  if (!hint || !hint.key || !codes.length) return null;
  return { key: hint.key, target: codes[0] };
}

// [{ group, groupKey, tasks }] in template order, a group where its first task appears.
export function groupedTasks(view) {
  const groups = [];
  ((view && view.tasks) || []).forEach((task) => {
    let entry = groups.find((item) => item.group === task.group);
    if (!entry) {
      entry = { group: task.group, groupKey: groupKey(task.group), tasks: [] };
      groups.push(entry);
    }
    entry.tasks.push(task);
  });
  return groups;
}

// The approval panel (D2): the blocking task codes, whether the reader is a Manager/Admin
// (canCorrect is exactly that role check in the API view), and whether "Approva bonifica" and
// "Accetta eccezione" are enabled (the view's canApprove / canAcceptExceptions; the API checks
// again).
export function approvalPanel(view) {
  const blocking =
    (view && view.approvalCheck && view.approvalCheck.blocking) || [];
  const codes = (code) => {
    const item = blocking.find((entry) => entry && entry.code === code);
    return item ? [...(item.taskCodes || [])] : [];
  };
  const approved = Boolean(view && view.approval);
  return {
    manager: Boolean(view && view.canCorrect === true),
    approved,
    missing: approved ? [] : codes("mandatory_missing"),
    exceptions: approved ? [] : codes("exception_pending"),
    canApprove: Boolean(view && !approved && view.canApprove === true),
    canAccept: Boolean(view && !approved && view.canAcceptExceptions === true),
  };
}

// The green banner of an approved checklist (M5): { name, date, version, dueDate, timing }, with
// dueDate "" without a due date and timing "on_time" | "late" from the view's deadline (the same
// source as the deadline chip). Null before the approval.
export function approvalBanner(view) {
  const approval = view && view.approval;
  if (!approval) return null;
  const deadline = view.deadline || {};
  return {
    name: (approval.approvedBy && approval.approvedBy.name) || "",
    date: formatCivilDate(approval.approvedDateRome),
    version: approval.templateVersion,
    dueDate: formatCivilDate(deadline.dueDate),
    timing: deadline.status === "approved_late" ? "late" : "on_time",
  };
}

// The approved summary list (D2): { done, total, optional, groups }, with done / total over the
// mandatory tasks, optional the number of optional tasks, and per group (template order) one row
// per task: { code, mandatory, status, quantity, reason, evidence }. A row shows its quantity
// ({ value, unit, basis }) or else its reason; evidence is the number of files.
export function approvedSummary(view) {
  const tasks = (view && view.tasks) || [];
  const { done, total } = counters(view);
  return {
    done,
    total,
    optional: tasks.filter((task) => !task.mandatory).length,
    groups: groupedTasks(view).map((group) => ({
      group: group.group,
      groupKey: group.groupKey,
      rows: group.tasks.map((task) => {
        const current = task.current;
        return {
          code: task.code,
          mandatory: Boolean(task.mandatory),
          status: taskStatus(task, null),
          quantity: (current && current.quantity) || null,
          reason:
            current && !current.quantity && current.reason
              ? current.reason
              : "",
          evidence: ((current && current.evidenceFileIds) || []).length,
        };
      }),
    })),
  };
}

// A failed GET /workshop/checklists/:id -> "not_found" for any 404 (missing, another
// institution's, or the module off), "error" for anything else.
export function checklistLoadState(err) {
  const res = err && err.response;
  return res && res.status === 404 ? "not_found" : "error";
}

// A failed GET /workshop-checklist -> "no_checklist" when the confirmed dossier has no checklist
// yet (the backfill has not run), "error" for anything else.
export function workshopLoadState(err) {
  const res = err && err.response;
  return res &&
    res.status === 404 &&
    res.data &&
    res.data.code === "no_checklist"
    ? "no_checklist"
    : "error";
}

// What the card shows: "loading", "no_checklist", "error" or "checklist".
export function cardState(loadState) {
  return loadState || "loading";
}

// The "Bonifica" cell of both vehicle lists from one by-cars row: { chip, dueDate }.
// chip is { key, params, color, title } or null (shown as "—"); dueDate is "DD/MM/YYYY" or "".
// Unapproved and past due shows "In ritardo x/y" in red instead of the state.
export function listCell(row) {
  if (!row || row.status !== "confirmed") return { chip: null, dueDate: "" };
  const deadline = row.deadlines && row.deadlines.depollution;
  const dueDate = formatCivilDate(deadline && deadline.dueDate);
  const workshop = row.workshop;
  if (!workshop || !WORKSHOP_STATE_COLORS[workshop.state]) {
    return { chip: null, dueDate };
  }
  const params = { done: workshop.done, total: workshop.total };
  const overdue =
    workshop.state !== "approved" && workshop.deadlineStatus === "overdue";
  let title = null;
  if (
    workshop.state === "approved" &&
    workshop.deadlineStatus === "approved_late"
  ) {
    title = { key: "acceptance.depollution.list.approved_late", params: {} };
  } else if (workshop.pendingExceptions > 0) {
    title = {
      key: "acceptance.depollution.list.pending_exceptions",
      params: { count: workshop.pendingExceptions },
    };
  }
  return {
    chip: {
      key: overdue
        ? "acceptance.depollution.list.overdue"
        : `acceptance.depollution.list.${workshop.state}`,
      params,
      color: overdue ? "red" : WORKSHOP_STATE_COLORS[workshop.state],
      title,
    },
    dueDate,
  };
}
